import { Input } from 'antd';
import type { InputRef } from 'antd';
import { forwardRef, useEffect, useState } from 'react';
import { normalisePhone } from '@pams/shared';

/**
 * FR-5.8.3b — "The UI accepts a paste of any format, normalises it in place, and
 * displays the resulting 10 digits. Typed entry is numeric-only and capped at 10.
 * Rejection states what was wrong rather than clearing the field."
 *
 * It calls the ONE shared `normalisePhone` (FR-5.8.3a). If the browser normalised
 * differently from the server, uniqueness would break in a way that presents as an
 * intermittent login bug — someone registers fine and then cannot sign in, sometimes.
 *
 * ⚠️ THERE IS NO `maxLength` ON THE DOM INPUT, AND THERE MUST NOT BE.
 *
 * The browser truncates a PASTED `+919876543210` to `+91987654` before any JavaScript
 * runs, and the normaliser then rejects a string the user never typed. The cap belongs
 * in the change handler, which only sees typed input.
 *
 * This component renders no error text of its own: the message comes from the schema via
 * `Form.Item`, and the schema delegates to the same `normalisePhone`. One source for the
 * wording, one place it appears.
 *
 * There is deliberately NO `onBlur` normalisation. It would be unreachable: paste is
 * already normalised at paste time, and typed entry can never exceed ten digits, so
 * there is no state a blur handler could still fix. Dead code that looks defensive is
 * worse than no code (CLAUDE.md §9).
 */
export type PhoneInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
};

export const PhoneInput = forwardRef<InputRef, PhoneInputProps>(
  ({ value, onChange, placeholder = 'Mobile number', disabled }, ref) => {
    const [text, setText] = useState(value ?? '');

    useEffect(() => setText(value ?? ''), [value]);

    const commit = (next: string) => {
      setText(next);
      onChange?.(next);
    };

    return (
      <Input
        ref={ref}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        inputMode="numeric"
        autoComplete="tel"
        data-testid="phone-input"
        onChange={(event) => {
          /**
           * Typed entry only: digits, capped at ten. Full normalisation deliberately
           * does NOT run here — stripping a leading `0` mid-typing would eat a digit the
           * user is still in the middle of entering.
           */
          commit(event.target.value.replace(/\D/g, '').slice(0, 10));
        }}
        onPaste={(event) => {
          event.preventDefault();
          const pasted = event.clipboardData.getData('text');
          const result = normalisePhone(pasted);
          // Accepted: show the canonical ten digits. Rejected: keep what they pasted, so
          // the Form.Item message can explain what was wrong with it rather than the
          // field simply emptying itself.
          commit(result.ok ? result.phone : pasted.trim());
        }}
      />
    );
  },
);

PhoneInput.displayName = 'PhoneInput';
